// Copyright (c) Microsoft. All rights reserved.

import { FC, useCallback, useState } from 'react';

import { useMsal } from '@azure/msal-react';
import {
    Avatar,
    Button,
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Persona,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { Settings24Regular } from '@fluentui/react-icons';
import { AuthHelper, Roles } from '../../libs/auth/AuthHelper';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState, resetState } from '../../redux/app/store';
import { FeatureKeys } from '../../redux/features/app/AppState';
import { SettingsDialog } from './settings-dialog/SettingsDialog';
import { ChatService } from '../../libs/services/ChatService';

export const useClasses = makeStyles({
    root: {
        marginRight: tokens.spacingHorizontalXL,
    },
    persona: {
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingVerticalMNudge),
        overflowWrap: 'break-word',
    },
});

interface IUserSettingsProps {
    setLoadingState: () => void;
}

export const AdminConsoleAppUrl =
    process.env.REACT_APP_ADMIN_CONSOLE_APP_URL == null || process.env.REACT_APP_ADMIN_CONSOLE_APP_URL.trim() === ''
        ? window.origin
        : process.env.REACT_APP_ADMIN_CONSOLE_APP_URL;

export const UserSettingsMenu: FC<IUserSettingsProps> = ({ setLoadingState }) => {
    const classes = useClasses();
    const { instance, inProgress } = useMsal();

    const { activeUserInfo, features } = useAppSelector((state: RootState) => state.app);

    const [openSettingsDialog, setOpenSettingsDialog] = useState(false);

    const onLogout = useCallback(() => {
        setLoadingState();
        AuthHelper.logoutAsync(instance);
        resetState();
    }, [instance, setLoadingState]);


 const onRefresh = useCallback(async () => {
        setLoadingState();
        
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            if (accessToken && activeUserInfo?.id) {

                const chatService = new ChatService();
                await chatService.clearUserCacheAsync(activeUserInfo.id, accessToken);
                window.location.reload();
            }
        } catch (error) {

            // auf deutsch
            console.error('Cache aktualisieren fehlgeschlagen:', error);
        } finally {

        }
    }, [instance, inProgress, setLoadingState, activeUserInfo?.id]);

     // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const isAdmin = activeUserInfo?.roles.includes(Roles.Admin)?? false;
    const isTenantAdmin = activeUserInfo?.roles.includes(Roles.TenantAdmin)?? false;

const openAdminConsole = () => {
    try {
        const adminConsoleUrl = AdminConsoleAppUrl;

        window.open(adminConsoleUrl, '_blank');
    } catch (error) {
        console.error('Failed to open Admin Console:', error);
        if (error instanceof Error) {
            alert(error.message); // Display an alert with the error message
        } else {
            alert('An unknown error occurred'); // Fallback for non-Error types
        }
    }
};

    return (
        <>
            {AuthHelper.isAuthAAD() ? (
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        {
                            <Avatar
                                className={classes.root}
                                key={activeUserInfo?.username}
                                name={activeUserInfo?.username}
                                size={28}
                                badge={
                                    !features[FeatureKeys.SimplifiedExperience].enabled
                                        ? { status: 'available' }
                                        : undefined
                                }
                                data-testid="userSettingsButton"
                            />
                        }
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            <Persona
                                className={classes.persona}
                                name={activeUserInfo?.username}
                                secondaryText={activeUserInfo?.email}
                                presence={
                                    !features[FeatureKeys.SimplifiedExperience].enabled
                                        ? { status: 'available' }
                                        : undefined
                                }
                                avatar={{ color: 'colorful' }}
                            />
                            <MenuDivider />
                            {isAdmin && (
                                <MenuItem
                                    data-testid="settingsMenuItem"
                                    onClick={() => {
                                        setOpenSettingsDialog(true);
                                    }}
                                >
                                    Einstellungen
                                </MenuItem>
                            )}

                    {(isAdmin || isTenantAdmin)  && (
                        <MenuItem
                            data-testid="adminConsoleMenuItem"
                            onClick={() => {
                                try {
                                    openAdminConsole();
                                } catch (error) {
                                    console.error('Error opening Admin Console:', error);
                                }
                            }}
                        >
                            Admin Center
                        </MenuItem>
                    )}       
                            <MenuItem data-testid="refreshMenuButton" onClick={() => { void onRefresh(); }}>
                                Aktualisieren Cache
                            </MenuItem>                   
                            <MenuItem data-testid="logOutMenuButton" onClick={onLogout}>
                                Abmelden
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            ) : (
                <Button
                    data-testid="settingsButtonWithoutAuth"
                    style={{ color: 'white' }}
                    appearance="transparent"
                    icon={<Settings24Regular color="white" />}
                    onClick={() => {
                        setOpenSettingsDialog(true);
                    }}
                >
                    Settings
                </Button>
            )}
            <SettingsDialog
                open={openSettingsDialog}
                closeDialog={() => {
                    setOpenSettingsDialog(false);
                }}
            />
        </>
    );
};
